import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../../components/layout/AdminLayout";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import {
  DollarSign,
  Percent,
  Truck,
  Globe,
  Save,
  Loader2,
} from "lucide-react";

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fxRates, setFxRates] = useState([]);
  const [customsRules, setCustomsRules] = useState([]);
  const [shippingRates, setShippingRates] = useState([]);
  const [pricingConfig, setPricingConfig] = useState({
    handling_fee: 5.0,
    min_profit_margin: 0.15,
    payment_fees: { paypal: 0.029 },
  });

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const [fxRes, customsRes, shippingRes, pricingRes] = await Promise.all([
        axios.get(`${API}/admin/fx-rates`, { withCredentials: true }),
        axios.get(`${API}/admin/customs-rules`, { withCredentials: true }),
        axios.get(`${API}/admin/shipping-rates`, { withCredentials: true }),
        axios.get(`${API}/admin/pricing-config`, { withCredentials: true }),
      ]);

      setFxRates(fxRes.data.fx_rates);
      setCustomsRules(customsRes.data.customs_rules);
      setShippingRates(shippingRes.data.shipping_rates);
      setPricingConfig(pricingRes.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleFxRateUpdate = async (fromCurrency, toCurrency, rate) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/fx-rates`,
        {
          from_currency: fromCurrency,
          to_currency: toCurrency,
          rate: parseFloat(rate),
          manual_override: true,
        },
        { withCredentials: true }
      );
      toast.success("FX rate updated");
      fetchAllSettings();
    } catch (error) {
      console.error("Error updating FX rate:", error);
      toast.error("Failed to update FX rate");
    } finally {
      setSaving(false);
    }
  };

  const handleShippingRateUpdate = async (destination, speed, baseRate, perLbRate) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/shipping-rates`,
        {
          destination_country: destination,
          speed: speed,
          base_rate: parseFloat(baseRate),
          per_lb_rate: parseFloat(perLbRate),
          carrier: "standard",
        },
        { withCredentials: true }
      );
      toast.success("Shipping rate updated");
      fetchAllSettings();
    } catch (error) {
      console.error("Error updating shipping rate:", error);
      toast.error("Failed to update shipping rate");
    } finally {
      setSaving(false);
    }
  };

  const handlePricingConfigUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pricing-config`, pricingConfig, {
        withCredentials: true,
      });
      toast.success("Pricing config updated");
    } catch (error) {
      console.error("Error updating pricing config:", error);
      toast.error("Failed to update pricing config");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-settings-title">
            Settings
          </h1>
          <p className="text-slate-600">
            Configure pricing, shipping, and customs rules
          </p>
        </div>

        <Tabs defaultValue="pricing" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pricing" data-testid="tab-pricing">
              <DollarSign className="h-4 w-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="fx" data-testid="tab-fx">
              <Globe className="h-4 w-4 mr-2" />
              FX Rates
            </TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping">
              <Truck className="h-4 w-4 mr-2" />
              Shipping
            </TabsTrigger>
            <TabsTrigger value="customs" data-testid="tab-customs">
              <Percent className="h-4 w-4 mr-2" />
              Customs
            </TabsTrigger>
          </TabsList>

          {/* Pricing Config */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle>Pricing Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="handling_fee">Handling Fee (USD)</Label>
                    <Input
                      id="handling_fee"
                      type="number"
                      step="0.01"
                      value={pricingConfig.handling_fee}
                      onChange={(e) =>
                        setPricingConfig({
                          ...pricingConfig,
                          handling_fee: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-testid="handling-fee-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="min_profit_margin">
                      Min Profit Margin (e.g., 0.15 = 15%)
                    </Label>
                    <Input
                      id="min_profit_margin"
                      type="number"
                      step="0.01"
                      value={pricingConfig.min_profit_margin}
                      onChange={(e) =>
                        setPricingConfig({
                          ...pricingConfig,
                          min_profit_margin: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-testid="profit-margin-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="paypal_fee">PayPal Fee Rate (e.g., 0.029 = 2.9%)</Label>
                    <Input
                      id="paypal_fee"
                      type="number"
                      step="0.001"
                      value={pricingConfig.payment_fees?.paypal || 0.029}
                      onChange={(e) =>
                        setPricingConfig({
                          ...pricingConfig,
                          payment_fees: {
                            ...pricingConfig.payment_fees,
                            paypal: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      data-testid="paypal-fee-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handlePricingConfigUpdate}
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="save-pricing-button"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Pricing Config
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FX Rates */}
          <TabsContent value="fx">
            <Card>
              <CardHeader>
                <CardTitle>Exchange Rates (Base: USD)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Override</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fxRates.map((rate, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{rate.from_currency}</TableCell>
                        <TableCell>{rate.to_currency}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.0001"
                            defaultValue={rate.rate}
                            className="w-32"
                            id={`fx-${rate.from_currency}-${rate.to_currency}`}
                            data-testid={`fx-rate-${rate.from_currency}-${rate.to_currency}`}
                          />
                        </TableCell>
                        <TableCell>
                          {rate.manual_override ? "Yes" : "No"}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const input = document.getElementById(
                                `fx-${rate.from_currency}-${rate.to_currency}`
                              );
                              handleFxRateUpdate(
                                rate.from_currency,
                                rate.to_currency,
                                input.value
                              );
                            }}
                            disabled={saving}
                          >
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shipping Rates */}
          <TabsContent value="shipping">
            <Card>
              <CardHeader>
                <CardTitle>Shipping Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Destination</TableHead>
                      <TableHead>Speed</TableHead>
                      <TableHead>Base Rate (USD)</TableHead>
                      <TableHead>Per Lb Rate (USD)</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shippingRates.map((rate, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{rate.destination_country}</TableCell>
                        <TableCell className="capitalize">{rate.speed}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={rate.base_rate}
                            className="w-24"
                            id={`ship-base-${rate.destination_country}-${rate.speed}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={rate.per_lb_rate}
                            className="w-24"
                            id={`ship-perlb-${rate.destination_country}-${rate.speed}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const baseInput = document.getElementById(
                                `ship-base-${rate.destination_country}-${rate.speed}`
                              );
                              const perLbInput = document.getElementById(
                                `ship-perlb-${rate.destination_country}-${rate.speed}`
                              );
                              handleShippingRateUpdate(
                                rate.destination_country,
                                rate.speed,
                                baseInput.value,
                                perLbInput.value
                              );
                            }}
                            disabled={saving}
                          >
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customs Rules */}
          <TabsContent value="customs">
            <Card>
              <CardHeader>
                <CardTitle>Customs Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {customsRules.map((rule, idx) => (
                    <div key={idx} className="p-4 border border-slate-200 rounded-sm">
                      <h3 className="font-bold text-lg mb-3">{rule.country_code}</h3>
                      {rule.weight_table ? (
                        <div>
                          <p className="text-sm text-slate-500 mb-2">
                            Weight-based customs (Barbados)
                          </p>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Min Weight (lbs)</TableHead>
                                <TableHead>Max Weight (lbs)</TableHead>
                                <TableHead>Fee (BBD)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rule.weight_table.map((bracket, i) => (
                                <TableRow key={i}>
                                  <TableCell>{bracket.min_weight}</TableCell>
                                  <TableCell>{bracket.max_weight}</TableCell>
                                  <TableCell>${bracket.fee}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <p className="text-sm text-slate-500 mt-2">
                            Minimum fee: ${rule.minimum_fee || 30} BBD
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-slate-500">
                            VAT/Import Rate: {((rule.vat_rate || 0) * 100).toFixed(1)}%
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
